import { ListingState } from "./types";

type MessageHandler = (message: MessageEvent<string>) => void;
type IsListingDataCurrent = (
  lastWebsocketMessagePublishedAt: string,
) => {
  isCurrent: boolean;
  lastWebsocketMessagePublishedAt: string;
};

const getHeartbeatInterval = (state: ListingState): number => {
  return state === "accepting_final_offers" ? 30000 : 60000;
};

class WebSocketConnection {
  private listingId: number;
  private listingState: ListingState;
  private isListingDataCurrent: IsListingDataCurrent;
  private socket: WebSocket;
  private refetch: () => void;
  private onMessage: MessageHandler;
  private heartbeatInterval: NodeJS.Timeout;
  private retryConnectionDelay: number;
  private isRetryingConnection: boolean;
  private retryConnectionTimeout: NodeJS.Timeout | null;

  constructor(
    listingId: number,
    listingState: ListingState,
    isListingDataCurrent: IsListingDataCurrent,
    refetch: () => void,
    onMessage: MessageHandler,
  ) {
    this.listingId = listingId;
    this.listingState = listingState;
    this.isListingDataCurrent = isListingDataCurrent;
    this.onMessage = onMessage;
    this.refetch = refetch;
    this.connect();
    this.retryConnectionDelay = 1000;
    this.isRetryingConnection = false;
    this.retryConnectionTimeout = null;
  }

  update(
    listingState: ListingState,
    isListingDataCurrent: IsListingDataCurrent,
    refetch: () => void,
    onMessage: MessageHandler,
  ) {
    const previousState = this.listingState;
    this.listingState = listingState;
    if (
      getHeartbeatInterval(this.listingState) !==
      getHeartbeatInterval(previousState)
    ) {
      this.stopHeartbeat();
      this.startHeartbeat();
    }
    this.isListingDataCurrent = isListingDataCurrent;
    this.refetch = refetch;
    this.onMessage = onMessage;
  }

  private connect() {
    this.retryConnectionTimeout = null;
    const socket = new WebSocket(
      `${import.meta.env.REACT_APP_LISTINGS_WEBSOCKET_URL}?listing_id=${
        this.listingId
      }`,
    );
    this.socket = socket;
    this.socket.onopen = () => {
      this.retryConnectionDelay = 1000;
      this.startHeartbeat();
      if (this.isRetryingConnection) {
        this.refetch();
      }
      this.isRetryingConnection = false;
    };

    this.socket.onmessage = event => {
      const payload = JSON.parse(event.data);
      if (payload.action === "heartbeat") {
        if (payload.data.status !== "connected") {
          this.socket.close();
        }

        const { isCurrent } = this.isListingDataCurrent(
          payload.data.websocket_data_published_at,
        );
        if (!isCurrent) {
          this.refetch();
        }
      } else if (payload.action === "state_update") {
        this.onMessage(event);
      }
    };

    const retryConnection = () => {
      this.isRetryingConnection = true;
      this.stopHeartbeat();
      if (!this.retryConnectionTimeout) {
        this.retryConnectionTimeout = setTimeout(() => {
          this.connect();
        }, this.retryConnectionDelay);
        // exponential backoff with a max delay of 60 seconds
        this.retryConnectionDelay = Math.min(
          this.retryConnectionDelay * 2,
          60000,
        );
      }
    };

    this.socket.onclose = () => {
      retryConnection();
    };

    this.socket.onerror = () => {
      retryConnection();
    };
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      try {
        this.socket.send(
          JSON.stringify({
            action: "heartbeat",
          }),
        );
      } catch {
        this.socket.close();
      }
    }, getHeartbeatInterval(this.listingState));
  }

  private stopHeartbeat() {
    clearInterval(this.heartbeatInterval);
  }

  checkStatus() {
    return this.socket.readyState === WebSocket.OPEN;
  }

  close() {
    this.stopHeartbeat();
    this.socket.close();
    this.isRetryingConnection = false;
    this.retryConnectionDelay = 1000;
    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onclose = null;
    this.socket.onerror = null;
  }
}

class WebSocketManager {
  private sockets: Map<number, WebSocketConnection>;

  constructor() {
    this.sockets = new Map();
  }

  clean() {
    this.sockets = new Map();
  }

  connect(
    listingId: number,
    listingState: ListingState,
    isListingDataCurrent: IsListingDataCurrent,
    onMessage: MessageHandler,
    refetch: () => void,
  ) {
    let socket = this.sockets.get(listingId);
    if (!socket) {
      socket = new WebSocketConnection(
        listingId,
        listingState,
        isListingDataCurrent,
        refetch,
        onMessage,
      );
      this.sockets.set(listingId, socket);
    } else {
      socket.update(listingState, isListingDataCurrent, refetch, onMessage);
    }
  }

  disconnect(listingId: number) {
    const socket = this.sockets.get(listingId);
    if (socket) {
      socket.close();
      this.sockets.delete(listingId);
    }
  }

  checkStatus(listingId: number) {
    const socket = this.sockets.get(listingId);
    return socket ? socket.checkStatus() : false;
  }
}

const websocketManager = new WebSocketManager();
export default websocketManager;
